/**
 * Bau-Bau Rytm (MgRhythm) — mini-gra WP12a (PLAN §14).
 * 3 tory, beatmapa, ±250 ms, brak game-over, wyjście zawsze.
 * Nagrody WYŁĄCZNIE przez core/RunClient (startRun/finishRun).
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { runClient } from '../../core/RunClient.js';
import { audioBus } from '../../core/AudioBus.js';
import {
  APPROACH_MS,
  GAME_ID,
  GOOD_STARS_THRESHOLD,
  HIT_WINDOW_MS,
  LANE_COUNT,
  MAX_STARS,
  ROUND_MS_DEFAULT,
  addStar,
  findHittableNote,
  generateBeatmap,
  isNoteExpired,
  returnSceneForEntry,
  scoreFromHits,
  spawnMsForNote,
  type Lane,
  type RhythmNote,
} from './rhythmLogic.js';

export type MgRhythmSceneData = {
  entryPoint?: 'phone' | 'console' | string;
  gameId?: string;
};

type LiveNote = {
  def: RhythmNote;
  go: Phaser.GameObjects.Container;
};

const LANE_COLORS = [0xff8fab, 0xbde0fe, 0xc3f0ca] as const;
const LANE_KEYS_NUM = ['ONE', 'TWO', 'THREE'] as const;
const LANE_KEYS_ASD = ['A', 'S', 'D'] as const;

export class MgRhythmScene extends BaseScene {
  static readonly KEY = 'MgRhythm';

  private entryPoint: string | undefined;
  private runId: string | null = null;
  private startedAt = 0;
  private roundMs = ROUND_MS_DEFAULT;
  private notes: RhythmNote[] = [];
  private resolved = new Set<string>();
  private live = new Map<string, LiveNote>();
  private stars = 0;
  private hits = 0;
  private finishing = false;
  private playing = false;

  private starsText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private hitLineY = 0;
  private laneXs: number[] = [];
  private spawnY = 0;
  private keys: Phaser.Input.Keyboard.Key[] = [];

  constructor() {
    super(MgRhythmScene.KEY);
  }

  init(data: MgRhythmSceneData = {}): void {
    this.entryPoint = data.entryPoint;
    this.runId = null;
    this.startedAt = 0;
    this.notes = [];
    this.resolved = new Set();
    this.live = new Map();
    this.stars = 0;
    this.hits = 0;
    this.finishing = false;
    this.playing = false;
    this.keys = [];
  }

  create(data: MgRhythmSceneData = {}): void {
    this.entryPoint = data.entryPoint ?? this.entryPoint;
    audioBus.unlock();

    this.cameras.main.setBackgroundColor(0xfff0f5);
    this.roundMs = ROUND_MS_DEFAULT;
    this.notes = generateBeatmap({
      bpm: 90,
      durationMs: this.roundMs,
      seed: Date.now() % 10_000,
    });

    const cx = this.gameW / 2;
    const topPad = 56;
    this.spawnY = 100;
    this.hitLineY = this.gameH - 180;

    // tytuł
    this.add
      .text(cx, 28, 'Bau-Bau Rytm 🎵', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '36px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.starsText = this.add
      .text(24, topPad, '⭐ 0', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);

    this.timerText = this.add
      .text(this.gameW - 24, topPad, this.formatTime(this.roundMs), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5);

    this.hintText = this.add
      .text(cx, 72, 'Stukaj w takt! Pudło = plum, bez kary 💜', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5);

    // linia trafienia
    this.add
      .rectangle(cx, this.hitLineY, this.gameW * 0.85, 6, 0xffffff)
      .setStrokeStyle(2, 0xff69b4);

    // tory + cele (≥64 px)
    const gap = Math.min(280, this.gameW / (LANE_COUNT + 1));
    this.laneXs = [];
    for (let lane = 0; lane < LANE_COUNT; lane++) {
      const x = cx + (lane - 1) * gap;
      this.laneXs.push(x);
      this.buildLane(lane as Lane, x);
    }

    // wyjście zawsze dostępne
    UiKit.button(this, {
      x: this.gameW - 100,
      y: this.gameH - 48,
      width: 160,
      height: MIN_TOUCH,
      label: 'Wyjdź',
      fill: 0xe0d4f0,
      onClick: () => {
        void this.endRun('exit');
      },
    });

    // klawisze 1/2/3 i A/S/D
    if (this.input.keyboard) {
      for (let i = 0; i < LANE_COUNT; i++) {
        const k1 = this.input.keyboard.addKey(
          Phaser.Input.Keyboard.KeyCodes[LANE_KEYS_NUM[i]!],
        );
        const k2 = this.input.keyboard.addKey(
          Phaser.Input.Keyboard.KeyCodes[LANE_KEYS_ASD[i]!],
        );
        k1.on('down', () => this.onLaneTap(i as Lane));
        k2.on('down', () => this.onLaneTap(i as Lane));
        this.keys.push(k1, k2);
      }
    }

    void this.beginRun();
  }

  private buildLane(lane: Lane, x: number): void {
    const color = LANE_COLORS[lane] ?? 0xffb6c1;
    // tor (pas)
    this.add
      .rectangle(x, (this.spawnY + this.hitLineY) / 2, 88, this.hitLineY - this.spawnY + 40, color, 0.25)
      .setStrokeStyle(2, color);

    // strefa celu
    const zoneSize = Math.max(MIN_TOUCH, 80);
    const zone = this.add
      .circle(x, this.hitLineY, zoneSize / 2, color, 0.55)
      .setStrokeStyle(4, 0xffffff)
      .setInteractive({ useHandCursor: true });
    zone.on('pointerdown', () => this.onLaneTap(lane));

    // etykieta zero-reading (emoji + numer)
    const labels = ['1️⃣', '2️⃣', '3️⃣'];
    this.add
      .text(x, this.hitLineY, labels[lane] ?? '•', {
        fontSize: '28px',
      })
      .setOrigin(0.5);
  }

  private async beginRun(): Promise<void> {
    // zegar rundy od razu — nawet gdy startRun jest w locie (wyjście / offline)
    this.startedAt = this.time.now;
    this.playing = true;
    try {
      const { runId } = await runClient.startRun(GAME_ID);
      this.runId = runId;
    } catch {
      // offline / stub — i tak gramy; finish bez runId nie księguje zł (PLAN §7.5)
      this.runId = null;
    }
  }

  update(_time: number, _delta: number): void {
    if (!this.playing || this.finishing) return;

    const nowMs = this.time.now - this.startedAt;
    const left = Math.max(0, this.roundMs - nowMs);
    this.timerText.setText(this.formatTime(left));

    // spawn nut
    for (const n of this.notes) {
      if (this.resolved.has(n.id) || this.live.has(n.id)) continue;
      if (nowMs >= spawnMsForNote(n) && nowMs <= n.targetMs + HIT_WINDOW_MS) {
        this.spawnNoteVisual(n);
      }
    }

    // animacja spadania + wygasanie (plum)
    for (const [id, live] of this.live) {
      const n = live.def;
      const spawnAt = spawnMsForNote(n);
      const t = Phaser.Math.Clamp((nowMs - spawnAt) / APPROACH_MS, 0, 1.15);
      const y = Phaser.Math.Linear(this.spawnY, this.hitLineY, Math.min(t, 1));
      live.go.setY(y);
      live.go.setAlpha(t > 1 ? Math.max(0.2, 1 - (t - 1) * 3) : 1);

      if (!this.resolved.has(id) && isNoteExpired(n, nowMs)) {
        this.resolveMiss(n);
      }
    }

    if (nowMs >= this.roundMs) {
      void this.endRun('time');
    }
  }

  private spawnNoteVisual(note: RhythmNote): void {
    const x = this.laneXs[note.lane] ?? this.gameW / 2;
    const color = LANE_COLORS[note.lane] ?? 0xffb6c1;
    const size = Math.max(MIN_TOUCH * 0.85, 56);
    const disc = this.add.circle(0, 0, size / 2, color).setStrokeStyle(3, 0xffffff);
    const emoji = this.add.text(0, 0, '🐾', { fontSize: '28px' }).setOrigin(0.5);
    const go = this.add.container(x, this.spawnY, [disc, emoji]);
    go.setSize(size, size);
    this.live.set(note.id, { def: note, go });
  }

  private onLaneTap(lane: Lane): void {
    if (!this.playing || this.finishing) return;
    const nowMs = this.time.now - this.startedAt;
    const note = findHittableNote(this.notes, lane, nowMs, this.resolved);
    if (!note) {
      // puste tapnięcie — lekki feedback, bez kary
      this.flashLane(lane, 0xcccccc);
      return;
    }
    this.resolveHit(note);
  }

  private resolveHit(note: RhythmNote): void {
    if (this.resolved.has(note.id)) return;
    this.resolved.add(note.id);
    this.hits += 1;
    this.stars = addStar(this.stars, MAX_STARS);
    this.starsText.setText(`⭐ ${this.stars}`);
    this.flashLane(note.lane, 0xffd700);
    this.popFeedback(this.laneXs[note.lane]!, this.hitLineY - 40, '⭐', 0xffd700);
    this.destroyLive(note.id, true);
  }

  private resolveMiss(note: RhythmNote): void {
    if (this.resolved.has(note.id)) return;
    this.resolved.add(note.id);
    // plum bez kary — gwiazdki NIE spadają
    this.flashLane(note.lane, 0x9b7ebd);
    this.popFeedback(this.laneXs[note.lane]!, this.hitLineY - 20, 'plum', 0x9b7ebd);
    this.destroyLive(note.id, false);
  }

  private destroyLive(id: string, celebrate: boolean): void {
    const live = this.live.get(id);
    if (!live) return;
    this.live.delete(id);
    if (celebrate) {
      this.tweens.add({
        targets: live.go,
        scaleX: 1.4,
        scaleY: 1.4,
        alpha: 0,
        duration: 180,
        onComplete: () => live.go.destroy(),
      });
    } else {
      this.tweens.add({
        targets: live.go,
        alpha: 0,
        y: live.go.y + 30,
        duration: 220,
        onComplete: () => live.go.destroy(),
      });
    }
  }

  private flashLane(lane: Lane, color: number): void {
    const x = this.laneXs[lane] ?? 0;
    const flash = this.add.circle(x, this.hitLineY, 48, color, 0.7);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      scaleX: 1.6,
      scaleY: 1.6,
      duration: 250,
      onComplete: () => flash.destroy(),
    });
  }

  private popFeedback(x: number, y: number, text: string, color: number): void {
    const t = this.add
      .text(x, y, text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: text === 'plum' ? '22px' : '32px',
        color: text === 'plum' ? '#9b7ebd' : '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    void color;
    this.tweens.add({
      targets: t,
      y: y - 50,
      alpha: 0,
      duration: 500,
      onComplete: () => t.destroy(),
    });
  }

  private formatTime(ms: number): string {
    const s = Math.ceil(ms / 1000);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `⏱️ ${m}:${r.toString().padStart(2, '0')}`;
  }

  private async endRun(_reason: 'time' | 'exit'): Promise<void> {
    if (this.finishing) return;
    this.finishing = true;
    this.playing = false;

    const durationMs = Math.max(0, Math.round(this.time.now - this.startedAt));
    // taps: telemetria (rozstrzygnięte nuty); serwer i tak liczy nagrodę z gwiazdek
    const stats = {
      stars: this.stars,
      score: scoreFromHits(this.hits),
      durationMs,
      taps: this.resolved.size,
    };

    let rewardZl = 0;
    if (this.runId) {
      try {
        const result = await runClient.finishRun(this.runId, stats);
        rewardZl = result.rewardZl;
      } catch {
        rewardZl = 0;
      }
    }

    // krótki ekran podsumowania w tej samej scenie, potem Reward
    this.showLocalSummary(rewardZl);
  }

  private showLocalSummary(rewardZl: number): void {
    const cx = this.gameW / 2;
    const cy = this.gameH / 2;
    const panel = this.add.rectangle(cx, cy, 520, 320, 0xffffff, 0.95).setStrokeStyle(4, 0xffb6c1);
    void panel;

    const good = this.stars >= GOOD_STARS_THRESHOLD;
    const title = good ? 'Super rytm! 🌟' : 'Brawo! 🎉';
    const sub = good
      ? `Zdobyłaś ${this.stars} gwiazdek — świetna gra!`
      : `Masz ${this.stars} ⭐ — baw się dalej kiedy chcesz!`;

    this.add
      .text(cx, cy - 80, title, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '36px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(cx, cy - 20, sub, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#6b5f7a',
        align: 'center',
        wordWrap: { width: 460 },
      })
      .setOrigin(0.5);

    // NIGDY nie pokazujemy „przegrałaś" — tylko pozytyw
    this.add
      .text(cx, cy + 40, 'Brak przegranej — same gwiazdki! 💜', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#9b7ebd',
      })
      .setOrigin(0.5);

    const returnTo = returnSceneForEntry(this.entryPoint);
    UiKit.button(this, {
      x: cx,
      y: cy + 110,
      width: 260,
      height: MIN_TOUCH,
      label: 'Dalej',
      fill: 0xffb6c1,
      onClick: () => {
        getSceneRouter().go('Reward', { rewardZl, returnTo });
      },
    });
  }

  shutdown(): void {
    for (const k of this.keys) {
      k.removeAllListeners();
    }
    this.keys = [];
  }
}

export default MgRhythmScene;
